import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui';
import { CopyBlock, dracula } from '../../components/CodeBlock';

const BASE_URL =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_BASE) ||
  'https://api.walytic.com';

// ---------- Outreach examples ----------
const OUTREACH_LIST = `curl ${BASE_URL}/api/outreach \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "X-Workspace-Id: YOUR_WORKSPACE_ID"`;

const OUTREACH_CREATE = `curl -X POST ${BASE_URL}/api/outreach \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "X-Workspace-Id: YOUR_WORKSPACE_ID" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Apr launch nurture",
    "sessionIds": ["919876543210"],
    "stopOnReply": false,
    "messages": [
      { "delayHours": 0,  "text": "Hey {{firstName}}, here is the brochure." },
      { "delayHours": 48, "text": "Following up — any questions on pricing?" },
      { "delayHours": 168,"text": "One last nudge before I close the file." }
    ]
  }'`;

const OUTREACH_CREATE_JS = `await fetch("${BASE_URL}/api/outreach", {
  method: "POST",
  headers: {
    "x-api-key": process.env.WALYTIC_KEY,
    "X-Workspace-Id": process.env.WALYTIC_WORKSPACE_ID,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    name: "Trial onboarding",
    sessionIds: ["919876543210"],
    stopOnReply: true,            // engagement mode (default)
    messages: [
      { delayHours: 0,  text: "Welcome {{firstName}}!" },
      { delayHours: 24, text: "Need a hand setting things up?" },
    ],
  }),
});`;

const OUTREACH_UPDATE_NURTURE = `curl -X PUT ${BASE_URL}/api/outreach/CAMPAIGN_ID \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "X-Workspace-Id: YOUR_WORKSPACE_ID" \\
  -H "Content-Type: application/json" \\
  -d '{ "stopOnReply": false }'`;

const OUTREACH_DUPLICATE = `# Clone config only (recommended for templating)
curl -X POST ${BASE_URL}/api/outreach/CAMPAIGN_ID/duplicate \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "X-Workspace-Id: YOUR_WORKSPACE_ID"

# Clone config AND copy enrolled leads
curl -X POST ${BASE_URL}/api/outreach/CAMPAIGN_ID/duplicate \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "X-Workspace-Id: YOUR_WORKSPACE_ID" \\
  -H "Content-Type: application/json" \\
  -d '{ "withLeads": true }'`;

const OUTREACH_ASSIGN = `# Assign existing CRM contacts (canonical: contactIds[]; leads[] is also accepted as an alias)
curl -X POST ${BASE_URL}/api/outreach/CAMPAIGN_ID/assign \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "X-Workspace-Id: YOUR_WORKSPACE_ID" \\
  -H "Content-Type: application/json" \\
  -d '{ "contactIds": ["ck_ct_01", "ck_ct_02"] }'

# Remove contacts from a campaign (cancels pending sends, keeps the contact in CRM)
curl -X POST ${BASE_URL}/api/outreach/CAMPAIGN_ID/unassign \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "X-Workspace-Id: YOUR_WORKSPACE_ID" \\
  -H "Content-Type: application/json" \\
  -d '{ "contactIds": ["ck_ct_01"] }'

# Bulk-add by phone (JSON, parsed and upserted server-side)
curl -X POST ${BASE_URL}/api/outreach/CAMPAIGN_ID/assign-csv \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "X-Workspace-Id: YOUR_WORKSPACE_ID" \\
  -H "Content-Type: application/json" \\
  -d '{ "rows": [{ "phone": "919876543210", "firstName": "Asha" }, { "phone": "919812345678", "firstName": "Ravi" }] }'`;

const OUTREACH_TOGGLE = `curl -X PUT ${BASE_URL}/api/outreach/CAMPAIGN_ID/toggle \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "X-Workspace-Id: YOUR_WORKSPACE_ID"`;

const OUTREACH_DIAG = `curl ${BASE_URL}/api/outreach/CAMPAIGN_ID/diagnostics \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "X-Workspace-Id: YOUR_WORKSPACE_ID"`;

// ---------- Flows examples ----------
const FLOWS_LIST = `curl ${BASE_URL}/api/autoflows \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "X-Workspace-Id: YOUR_WORKSPACE_ID"`;

const FLOWS_CREATE = `curl -X POST ${BASE_URL}/api/autoflows \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "X-Workspace-Id: YOUR_WORKSPACE_ID" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Pricing keyword auto-reply",
    "sessionIds": ["919876543210"],
    "active": true,
    "nodes": [
      {
        "id": "n1",
        "type": "trigger",
        "config": { "match": "keyword", "keywords": ["pricing", "price"] }
      },
      {
        "id": "n2",
        "type": "sendMessage",
        "config": { "text": "Hi {{firstName}}, our pricing starts at $29/mo." }
      }
    ],
    "edges": [{ "from": "n1", "to": "n2" }]
  }'`;

const FLOWS_TOGGLE = `curl -X PUT ${BASE_URL}/api/autoflows/FLOW_ID/toggle \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "X-Workspace-Id: YOUR_WORKSPACE_ID"`;

const FLOWS_EXECUTIONS_JS = `// List recent flow runs and their state
const r = await fetch("${BASE_URL}/api/autoflows/FLOW_ID", {
  headers: {
    "x-api-key": process.env.WALYTIC_KEY,
    "X-Workspace-Id": process.env.WALYTIC_WORKSPACE_ID,
  },
});
const flow = await r.json();
console.log(flow.recentExecutions);
// [{ id, contact, state: "completed" | "waiting_for_reply" | ... , startedAt }]`;

// ---------- Webhook signing examples ----------
const WEBHOOK_VERIFY_NODE = `// Express middleware — verify X-Webhook-Signature
import crypto from "node:crypto";

export function verifyWalytic(secret) {
  return (req, res, next) => {
    const sigHeader = req.get("X-Webhook-Signature") || "";
    const sent = sigHeader.replace(/^sha256=/, "");
    const expected = crypto
      .createHmac("sha256", secret)
      .update(req.rawBody)        // make sure rawBody is captured
      .digest("hex");
    const ok =
      sent.length === expected.length &&
      crypto.timingSafeEqual(Buffer.from(sent), Buffer.from(expected));
    if (!ok) return res.status(401).send("bad signature");
    next();
  };
}`;

const WEBHOOK_VERIFY_PY = `# Flask handler — verify X-Webhook-Signature
import hmac, hashlib
from flask import request, abort

def verify_walytic(secret: str) -> None:
    sig = request.headers.get("X-Webhook-Signature", "")
    sent = sig.removeprefix("sha256=")
    expected = hmac.new(
        secret.encode(),
        request.get_data(),  # raw body bytes
        hashlib.sha256,
    ).hexdigest()
    if not hmac.compare_digest(sent, expected):
        abort(401)`;

const WEBHOOK_ROTATE = `curl -X POST ${BASE_URL}/api/webhook/WEBHOOK_ID/rotate-secret \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "X-Workspace-Id: YOUR_WORKSPACE_ID"
# Response:
# { "id": "...", "secret": "whsec_NEW_VALUE_SHOWN_ONCE" }`;

// ---------- Reply behaviour example ----------
const REPLY_SWITCH_TO_NURTURE = `curl -X PUT ${BASE_URL}/api/outreach/CAMPAIGN_ID \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "X-Workspace-Id: YOUR_WORKSPACE_ID" \\
  -H "Content-Type: application/json" \\
  -d '{ "stopOnReply": false }'`;

const SectionHeading = ({ id, children }) => (
  <h2 id={id} className="text-2xl font-bold mt-10 mb-3 scroll-mt-24">
    {children}
  </h2>
);

const Endpoint = ({ method, path, desc }) => (
  <tr className="border-t">
    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{method}</td>
    <td className="px-3 py-2 font-mono text-xs">{path}</td>
    <td className="px-3 py-2 text-sm text-gray-700">{desc}</td>
  </tr>
);

const EndpointTable = ({ rows }) => (
  <div className="overflow-x-auto my-3">
    <table className="w-full text-sm text-left">
      <thead className="bg-gray-50 text-gray-600">
        <tr>
          <th className="px-3 py-2 font-medium">Method</th>
          <th className="px-3 py-2 font-medium">Path</th>
          <th className="px-3 py-2 font-medium">What it does</th>
        </tr>
      </thead>
      <tbody>{rows}</tbody>
    </table>
  </div>
);

const GettingStarted = () => {
  return (
    <div className="p-2 md:p-4 max-w-4xl">
      <h1 className="text-3xl font-bold mb-2">Getting Started</h1>
      <p className="text-muted-foreground mb-6">
        A practical guide to the four feature surfaces most integrators reach
        for: outreach drips, flow-builder automations, webhooks, and
        reply-behaviour tuning. For the full per-endpoint reference, jump to
        the{' '}
        <Link to="/integration-and-api/swagger" className="text-sky-600 hover:underline">
          API Reference
        </Link>
        .
      </p>

      <Card className="p-4 my-4 bg-muted">
        <h2 className="font-semibold mb-2">Base URL &amp; auth</h2>
        <p className="text-sm text-gray-700 mb-2">
          All endpoints sit under <code className="font-mono">{BASE_URL}/api</code>.
          Authenticate every request with your{' '}
          <code className="font-mono">x-api-key</code> and{' '}
          <code className="font-mono">X-Workspace-Id</code> headers. Use{' '}
          <code className="font-mono">X-Client-Id</code> as an alias when an
          integration calls workspaces clients. See{' '}
          <Link to="/integration-and-api/authentication" className="text-sky-600 hover:underline">
            Authentication
          </Link>{' '}
          for key and workspace management.
        </p>
      </Card>

      {/* ===================== Outreach ===================== */}
      <SectionHeading id="outreach">Outreach API</SectionHeading>
      <p className="text-sm text-gray-700 mb-3">
        Outreach campaigns are multi-step WhatsApp drips. Each campaign holds
        an ordered list of <code>messages</code> with per-step{' '}
        <code>delayHours</code>, plus optional pacing windows and multi-session
        round-robin. Replies can either stop the sequence (engagement mode) or
        let it continue (nurture mode), controlled by the new{' '}
        <code>stopOnReply</code> flag.
      </p>

      <Card className="p-4 my-4">
        <h3 className="font-semibold mb-2">Endpoints</h3>
        <EndpointTable
          rows={
            <>
              <Endpoint method="GET" path="/api/outreach" desc="List campaigns in the workspace." />
              <Endpoint method="POST" path="/api/outreach" desc="Create a campaign (defaults to active=false until you toggle on)." />
              <Endpoint method="GET" path="/api/outreach/:id" desc="Fetch one campaign with stats." />
              <Endpoint method="PUT" path="/api/outreach/:id" desc="Update name, schedule, sessions, stopOnReply, messages." />
              <Endpoint method="DELETE" path="/api/outreach/:id" desc="Delete a campaign and its enrolments." />
              <Endpoint method="PUT" path="/api/outreach/:id/toggle" desc="Flip active on/off." />
              <Endpoint method="POST" path="/api/outreach/:id/duplicate" desc="Clone the campaign config; pass { withLeads: true } to copy enrolments. Always lands as active=false." />
              <Endpoint method="POST" path="/api/outreach/:id/assign" desc="Enrol one contact." />
              <Endpoint method="POST" path="/api/outreach/:id/assign-csv" desc="Bulk-enrol from CSV (multipart upload)." />
              <Endpoint method="POST" path="/api/outreach/:id/unassign" desc="Remove a contact from the sequence." />
              <Endpoint method="GET" path="/api/outreach/:id/diagnostics" desc="Per-step throughput, failure breakdown, send-window utilisation." />
              <Endpoint method="GET" path="/api/outreach/:id/events" desc="Recent send / reply / fail events." />
              <Endpoint method="POST" path="/api/outreach/:id/messages" desc="Append a message step." />
              <Endpoint method="PUT" path="/api/outreach/:id/messages/:idx" desc="Edit one step." />
              <Endpoint method="DELETE" path="/api/outreach/:id/messages/:idx" desc="Remove one step." />
            </>
          }
        />
      </Card>

      <Card className="p-4 my-4">
        <h3 className="font-semibold mb-2">Media handling</h3>
        <p className="text-sm text-gray-700 mb-2">
          Each step accepts an optional <code>mediaUrl</code>. The backend
          inspects the URL extension and routes the asset into the correct
          WhatsApp envelope so recipients see thumbnails for images, inline
          players for video/audio, and real filenames for documents — no more
          generic "file" pills.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-3 py-2 font-medium">Extension</th>
                <th className="px-3 py-2 font-medium">Renders as</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              <tr className="border-t">
                <td className="px-3 py-2 font-mono">.jpg .jpeg .png .webp .gif</td>
                <td className="px-3 py-2">Image with thumbnail</td>
              </tr>
              <tr className="border-t">
                <td className="px-3 py-2 font-mono">.mp4 .mov .3gp</td>
                <td className="px-3 py-2">Inline video player</td>
              </tr>
              <tr className="border-t">
                <td className="px-3 py-2 font-mono">.ogg .mp3 .m4a .aac .wav</td>
                <td className="px-3 py-2">Audio bubble (PTT for .ogg)</td>
              </tr>
              <tr className="border-t">
                <td className="px-3 py-2 font-mono">.pdf .docx .xlsx .csv .zip ...</td>
                <td className="px-3 py-2">Document with original filename</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-4 my-4">
        <h3 className="font-semibold mb-2">Create &mdash; nurture mode</h3>
        <CopyBlock language="bash" text={OUTREACH_CREATE} theme={dracula} wrapLines />
        <p className="text-sm text-gray-700 mt-3 mb-1">JS / fetch:</p>
        <CopyBlock language="javascript" text={OUTREACH_CREATE_JS} theme={dracula} wrapLines />
      </Card>

      <Card className="p-4 my-4">
        <h3 className="font-semibold mb-2">List, toggle, diagnose</h3>
        <CopyBlock language="bash" text={OUTREACH_LIST} theme={dracula} wrapLines />
        <p className="text-sm text-gray-700 mt-3 mb-1">Toggle on/off:</p>
        <CopyBlock language="bash" text={OUTREACH_TOGGLE} theme={dracula} wrapLines />
        <p className="text-sm text-gray-700 mt-3 mb-1">Diagnostics (per-step deliverability):</p>
        <CopyBlock language="bash" text={OUTREACH_DIAG} theme={dracula} wrapLines />
      </Card>

      <Card className="p-4 my-4">
        <h3 className="font-semibold mb-2">Duplicate</h3>
        <p className="text-sm text-gray-700 mb-2">
          Useful for templating winning sequences across markets. Pass{' '}
          <code>withLeads: true</code> to copy enrolled leads as well; without
          it, only the configuration is cloned. The duplicate always lands as{' '}
          <code>active: false</code> so you can review before launch.
        </p>
        <CopyBlock language="bash" text={OUTREACH_DUPLICATE} theme={dracula} wrapLines />
      </Card>

      <Card className="p-4 my-4">
        <h3 className="font-semibold mb-2">Enrol contacts</h3>
        <CopyBlock language="bash" text={OUTREACH_ASSIGN} theme={dracula} wrapLines />
      </Card>

      <Card className="p-4 my-4 border-l-4 border-emerald-400 bg-emerald-50">
        <h3 className="font-semibold mb-1 text-emerald-900">
          Recovery sweep
        </h3>
        <p className="text-sm text-emerald-900">
          If the backend restarts mid-campaign, executions whose delayed jobs
          were lost are automatically rescheduled within 60 seconds at their
          original <code>nextSendAt</code>. No manual intervention required.
        </p>
      </Card>

      {/* ===================== Reply Behaviour ===================== */}
      <SectionHeading id="reply-behaviour">Reply Behaviour</SectionHeading>
      <p className="text-sm text-gray-700 mb-3">
        The <code>stopOnReply</code> flag on an outreach campaign chooses
        between two operating modes. Replies are always tracked in{' '}
        <code>OutreachCampaignStats.replied</code> regardless of mode — the
        flag only affects whether the sequence keeps sending to that contact.
      </p>

      <Card className="p-4 my-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-3 py-2 font-medium">Mode</th>
                <th className="px-3 py-2 font-medium">stopOnReply</th>
                <th className="px-3 py-2 font-medium">Behaviour on first reply</th>
                <th className="px-3 py-2 font-medium">Best for</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              <tr className="border-t">
                <td className="px-3 py-2 font-medium">Engagement (default)</td>
                <td className="px-3 py-2 font-mono">true</td>
                <td className="px-3 py-2">Sequence stops for that contact; future steps are skipped.</td>
                <td className="px-3 py-2">Sales outreach, qualification</td>
              </tr>
              <tr className="border-t">
                <td className="px-3 py-2 font-medium">Nurture</td>
                <td className="px-3 py-2 font-mono">false</td>
                <td className="px-3 py-2">Sequence continues at its own cadence — replies are recorded but do not interrupt the drip.</td>
                <td className="px-3 py-2">Long-form education, post-purchase, monthly tips</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-4 my-4">
        <h3 className="font-semibold mb-2">Switch an existing campaign to nurture mode</h3>
        <CopyBlock language="bash" text={REPLY_SWITCH_TO_NURTURE} theme={dracula} wrapLines />
        <p className="text-xs text-gray-500 mt-2">
          The change applies to every contact still enrolled, including those
          mid-sequence.
        </p>
      </Card>

      {/* ===================== Flows ===================== */}
      <SectionHeading id="flows">Flows API</SectionHeading>
      <p className="text-sm text-gray-700 mb-3">
        AutoFlows are the visual flow builder for branched WhatsApp
        automations. A flow is a directed graph of typed nodes wired together
        with edges. The runtime tracks each contact's traversal as a{' '}
        <code>FlowExecution</code>.
      </p>

      <Card className="p-4 my-4">
        <h3 className="font-semibold mb-2">Endpoints</h3>
        <EndpointTable
          rows={
            <>
              <Endpoint method="GET" path="/api/autoflows" desc="List flows in the workspace." />
              <Endpoint method="POST" path="/api/autoflows" desc="Create a flow (nodes, edges, triggers)." />
              <Endpoint method="GET" path="/api/autoflows/:id" desc="Fetch one flow plus recent executions." />
              <Endpoint method="PUT" path="/api/autoflows/:id" desc="Update graph or settings." />
              <Endpoint method="DELETE" path="/api/autoflows/:id" desc="Delete the flow." />
              <Endpoint method="PUT" path="/api/autoflows/:id/toggle" desc="Flip active on/off." />
            </>
          }
        />
      </Card>

      <Card className="p-4 my-4">
        <h3 className="font-semibold mb-2">Node catalogue</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Purpose</th>
                <th className="px-3 py-2 font-medium">Key config</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              <tr className="border-t">
                <td className="px-3 py-2 font-mono">trigger</td>
                <td className="px-3 py-2">Entry point — keyword, inbound message, or external trigger.</td>
                <td className="px-3 py-2"><code>match</code>, <code>keywords</code></td>
              </tr>
              <tr className="border-t">
                <td className="px-3 py-2 font-mono">sendMessage</td>
                <td className="px-3 py-2">Send text and/or media.</td>
                <td className="px-3 py-2"><code>text</code>, <code>mediaUrl</code></td>
              </tr>
              <tr className="border-t">
                <td className="px-3 py-2 font-mono">delay</td>
                <td className="px-3 py-2">Wait a fixed duration before continuing.</td>
                <td className="px-3 py-2"><code>seconds</code> / <code>minutes</code> / <code>hours</code></td>
              </tr>
              <tr className="border-t">
                <td className="px-3 py-2 font-mono">condition</td>
                <td className="px-3 py-2">Branch on variable equality, contains, regex.</td>
                <td className="px-3 py-2"><code>left</code>, <code>op</code>, <code>right</code></td>
              </tr>
              <tr className="border-t">
                <td className="px-3 py-2 font-mono">sendToAI</td>
                <td className="px-3 py-2">One-shot LLM call; result is exposed as <code>{'{{node.output}}'}</code>.</td>
                <td className="px-3 py-2"><code>prompt</code>, <code>model</code></td>
              </tr>
              <tr className="border-t">
                <td className="px-3 py-2 font-mono">aiConversation</td>
                <td className="px-3 py-2">Multi-turn AI dialogue with the contact until exit criteria.</td>
                <td className="px-3 py-2"><code>systemPrompt</code>, <code>exitOn</code></td>
              </tr>
              <tr className="border-t">
                <td className="px-3 py-2 font-mono">waitForReply</td>
                <td className="px-3 py-2">Pause until the contact replies (or timeout).</td>
                <td className="px-3 py-2"><code>timeoutMinutes</code></td>
              </tr>
              <tr className="border-t">
                <td className="px-3 py-2 font-mono">loop</td>
                <td className="px-3 py-2">Iterate over a list variable, executing the loop body per item.</td>
                <td className="px-3 py-2"><code>over</code>, <code>maxIterations</code></td>
              </tr>
              <tr className="border-t">
                <td className="px-3 py-2 font-mono">triggerWebhook</td>
                <td className="px-3 py-2">Fire a registered Walytic webhook event from inside a flow.</td>
                <td className="px-3 py-2"><code>event</code>, <code>payload</code></td>
              </tr>
              <tr className="border-t">
                <td className="px-3 py-2 font-mono">httpRequest</td>
                <td className="px-3 py-2">Outbound GET/POST/PUT/PATCH/DELETE with custom headers and body. SSRF-protected: blocks internal IPs and the Walytic admin API.</td>
                <td className="px-3 py-2"><code>method</code>, <code>url</code>, <code>headers</code>, <code>body</code></td>
              </tr>
              <tr className="border-t">
                <td className="px-3 py-2 font-mono">sendVoiceNote</td>
                <td className="px-3 py-2">TTS via ElevenLabs or OpenAI; output is OGG Opus for native WhatsApp playback.</td>
                <td className="px-3 py-2"><code>provider</code>, <code>voiceId</code>, <code>text</code></td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-4 my-4">
        <h3 className="font-semibold mb-2">Variables</h3>
        <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
          <li><code className="font-mono">{'{{firstName}}'}</code> — pulled from the matched contact record.</li>
          <li><code className="font-mono">{'{{prev.output}}'}</code> — output of the previous node in the traversal.</li>
          <li><code className="font-mono">{'{{nodeId.output}}'}</code> — output of any specific upstream node by id (e.g. <code>{'{{n3.output}}'}</code>). Useful when branching back together.</li>
        </ul>
        <p className="text-xs text-gray-500 mt-2">
          Variables interpolate in <code>sendMessage.text</code>, <code>httpRequest.url/body</code>, and most other string fields.
        </p>
      </Card>

      <Card className="p-4 my-4">
        <h3 className="font-semibold mb-2">FlowExecution states</h3>
        <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
          <li><code>running</code> — actively traversing nodes.</li>
          <li><code>delayed</code> — sleeping inside a <code>delay</code> node.</li>
          <li><code>waiting_for_reply</code> — paused at a <code>waitForReply</code>; resumes on inbound message.</li>
          <li><code>ai_conversation</code> — inside an <code>aiConversation</code> node.</li>
          <li><code>completed</code> — reached a terminal node.</li>
          <li><code>errored</code> — a node threw; see <code>lastError</code> on the execution.</li>
          <li><code>expired</code> — a wait timed out before the contact replied.</li>
        </ul>
      </Card>

      <Card className="p-4 my-4">
        <h3 className="font-semibold mb-2">List flows</h3>
        <CopyBlock language="bash" text={FLOWS_LIST} theme={dracula} wrapLines />
      </Card>

      <Card className="p-4 my-4">
        <h3 className="font-semibold mb-2">Create a 2-node flow</h3>
        <CopyBlock language="bash" text={FLOWS_CREATE} theme={dracula} wrapLines />
      </Card>

      <Card className="p-4 my-4">
        <h3 className="font-semibold mb-2">Toggle &amp; inspect runs</h3>
        <CopyBlock language="bash" text={FLOWS_TOGGLE} theme={dracula} wrapLines />
        <p className="text-sm text-gray-700 mt-3 mb-1">Recent executions:</p>
        <CopyBlock language="javascript" text={FLOWS_EXECUTIONS_JS} theme={dracula} wrapLines />
      </Card>

      {/* ===================== Webhooks ===================== */}
      <SectionHeading id="webhooks">Webhooks &amp; HMAC signing</SectionHeading>
      <p className="text-sm text-gray-700 mb-3">
        Walytic posts events (incoming messages, delivery receipts, flow
        triggers, campaign completions, ...) to your registered webhook URL.
        Every delivery is signed so you can verify authenticity.
      </p>

      <Card className="p-4 my-4">
        <h3 className="font-semibold mb-2">Signature header</h3>
        <p className="text-sm text-gray-700 mb-2">
          Each delivery includes:
        </p>
        <CopyBlock
          language="bash"
          text={`X-Webhook-Signature: sha256=<hex_hmac_of_raw_body>`}
          theme={dracula}
          wrapLines
        />
        <p className="text-sm text-gray-700 mt-3">
          The HMAC is computed over the <strong>raw, unparsed</strong> request
          body using the per-webhook <code>secret</code> returned when you
          created the webhook. Compare in constant time and reject mismatches
          with <code>401</code>.
        </p>
      </Card>

      <Card className="p-4 my-4">
        <h3 className="font-semibold mb-2">Verify in Node.js</h3>
        <CopyBlock language="javascript" text={WEBHOOK_VERIFY_NODE} theme={dracula} wrapLines />
      </Card>

      <Card className="p-4 my-4">
        <h3 className="font-semibold mb-2">Verify in Python</h3>
        <CopyBlock language="python" text={WEBHOOK_VERIFY_PY} theme={dracula} wrapLines />
      </Card>

      <Card className="p-4 my-4">
        <h3 className="font-semibold mb-2">Rotate a compromised secret</h3>
        <p className="text-sm text-gray-700 mb-2">
          If a secret leaks (logs, screenshots, lost laptop), rotate it. The
          new secret is shown <strong>once</strong> in the response — store it
          immediately.
        </p>
        <CopyBlock language="bash" text={WEBHOOK_ROTATE} theme={dracula} wrapLines />
        <p className="text-xs text-gray-500 mt-2">
          Old signatures are invalid the moment rotation completes; deploy the
          new secret to all listeners before triggering rotation.
        </p>
      </Card>

      <Card className="p-4 my-4 bg-muted">
        <h3 className="font-semibold mb-2">Need every endpoint?</h3>
        <p className="text-sm text-gray-700">
          The full per-endpoint reference, including request/response schemas
          and an interactive Try It panel, lives at{' '}
          <Link to="/integration-and-api/swagger" className="text-sky-600 hover:underline">
            API Reference
          </Link>
          . It always reflects the current OpenAPI spec at{' '}
          <code className="font-mono">/api/openapi.json</code>.
        </p>
      </Card>
    </div>
  );
};

export default GettingStarted;
